import { useEffect, useRef, useState, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Download, Copy, ExternalLink, Check } from 'lucide-react'
import { generateQrDataUrl, buildPayload, type SepaQrData } from '@/lib/sepa-qr'
import { buildUrl, buildEmbedSnippet } from '@/lib/url-params'

interface QrCodeDisplayProps {
  data: SepaQrData
  isValid: boolean
  cleanMode?: boolean
  shareMode?: boolean
}

/**
 * QR code display component with download and embed options
 */
export function QrCodeDisplay({ data, isValid, cleanMode = false, shareMode = false }: QrCodeDisplayProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [copied, setCopied] = useState<'url' | 'embed' | 'payload' | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Generate QR code when data changes
  useEffect(() => {
    if (!isValid) {
      setQrDataUrl('')
      return
    }

    generateQrDataUrl(data, { size: 300 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''))
  }, [data, isValid])

  // Copy to clipboard helper
  const copyToClipboard = useCallback(async (text: string, type: 'url' | 'embed' | 'payload') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    }
  }, [])

  // Download QR code as PNG
  const downloadQr = useCallback((size: number) => {
    if (!isValid) return

    generateQrDataUrl(data, { size }).then((url) => {
      const link = document.createElement('a')
      link.download = `sepa-rtp-${data.name.replace(/\s+/g, '-').toLowerCase()}-${size}px.png`
      link.href = url
      link.click()
    })
  }, [data, isValid])

  // Build shareable URL
  const shareUrl = buildUrl(data, window.location.origin, 'share')
  const cleanUrl = buildUrl(data, window.location.origin, 'clean')
  const embedSnippet = buildEmbedSnippet(cleanUrl)
  const payload = buildPayload(data)

  // Clean mode: just show QR (for embedding)
  if (cleanMode) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white p-4">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="SEPA QR Code" className="max-w-full" />
        ) : (
          <p className="text-muted-foreground">Invalid data</p>
        )}
      </div>
    )
  }

  // Share mode: QR code with payment summary
  if (shareMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-2">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-2">
                <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
              </div>
              <CardTitle className="text-xl">SEPA Payment Request</CardTitle>
              <CardDescription>Scan with your banking app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                {qrDataUrl ? (
                  <div className="p-4 bg-white rounded-xl shadow-inner border">
                    <img src={qrDataUrl} alt="SEPA QR Code" className="w-56 h-56" />
                  </div>
                ) : (
                  <p className="text-muted-foreground">Invalid data</p>
                )}
              </div>

              {/* Payment Summary */}
              <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-muted-foreground">Beneficiary</span>
                  <span className="text-sm font-medium text-right">{data.name}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-start">
                  <span className="text-sm text-muted-foreground">IBAN</span>
                  <span className="text-xs font-mono text-right break-all">{data.iban}</span>
                </div>
                {data.bic && (
                  <>
                    <Separator />
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-muted-foreground">BIC</span>
                      <span className="text-sm font-mono">{data.bic}</span>
                    </div>
                  </>
                )}
                {data.amount && (
                  <>
                    <Separator />
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-muted-foreground">Amount</span>
                      <span className="text-lg font-bold text-primary">EUR {data.amount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {data.reference && (
                  <>
                    <Separator />
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-muted-foreground">Reference</span>
                      <span className="text-sm font-mono text-right break-all">{data.reference}</span>
                    </div>
                  </>
                )}
                {data.text && (
                  <>
                    <Separator />
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-muted-foreground">Description</span>
                      <span className="text-sm text-right max-w-[200px]">{data.text}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <p className="text-xs text-center text-muted-foreground">
                EPC069-12 v3.1 • SEPA Credit Transfer
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <Card className="border-2 shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <CardTitle className="text-lg sm:text-xl">QR Code</CardTitle>
        </div>
        <CardDescription>
          Scan with your banking app to initiate payment
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6">
        {/* QR Code Display */}
        <div className="flex justify-center">
          {isValid && qrDataUrl ? (
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-0 group-hover:opacity-100" />
              <div className="relative p-4 sm:p-6 bg-white rounded-xl shadow-lg border-2 border-muted transition-transform duration-200 hover:scale-[1.02]">
                <img
                  src={qrDataUrl}
                  alt="SEPA QR Code"
                  className="w-48 h-48 sm:w-56 sm:h-56"
                />
              </div>
            </div>
          ) : (
            <div className="w-48 h-48 sm:w-56 sm:h-56 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50 rounded-xl border-2 border-dashed border-muted-foreground/30">
              <p className="text-muted-foreground text-xs sm:text-sm text-center px-4">
                Fill in the required fields to generate QR code
              </p>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {/* Data Summary */}
        {isValid && (
          <>
            <Separator />
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Payment Summary</h3>
              <div className="text-sm space-y-1 bg-muted/50 p-3 rounded-md font-mono text-xs">
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="text-muted-foreground">Beneficiary:</span>
                  <span className="font-medium">{data.name}</span>
                </div>
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="text-muted-foreground">IBAN:</span>
                  <span>{data.iban}</span>
                </div>
                {data.bic && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">BIC:</span>
                    <span>{data.bic}</span>
                  </div>
                )}
                {data.amount && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-medium">EUR {data.amount.toFixed(2)}</span>
                  </div>
                )}
                {data.purpose && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">Purpose:</span>
                    <span>{data.purpose}</span>
                  </div>
                )}
                {data.reference && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">Reference:</span>
                    <span>{data.reference}</span>
                  </div>
                )}
                {data.text && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">Text:</span>
                    <span className="break-all">{data.text}</span>
                  </div>
                )}
                {data.information && (
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <span className="text-muted-foreground">Info:</span>
                    <span>{data.information}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Download Options */}
            <Separator />
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Download</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadQr(256)}>
                  <Download className="h-4 w-4 mr-2" />
                  256px
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadQr(512)}>
                  <Download className="h-4 w-4 mr-2" />
                  512px
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadQr(1024)}>
                  <Download className="h-4 w-4 mr-2" />
                  1024px
                </Button>
              </div>
            </div>

            {/* Share & Embed */}
            <Separator />
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Share & Embed</h3>

              {/* Share URL */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Share URL</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                    {shareUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(shareUrl, 'url')}
                  >
                    {copied === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Embed Code */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Embed HTML</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                    {embedSnippet}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(embedSnippet, 'embed')}
                  >
                    {copied === 'embed' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Raw Payload */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">QR Payload (EPC069-12)</Label>
                <div className="flex gap-2">
                  <pre className="flex-1 text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre">
                    {payload}
                  </pre>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(payload, 'payload')}
                  >
                    {copied === 'payload' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Label component for this file
function Label({ className, children, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={`text-sm font-medium ${className || ''}`} {...props}>
      {children}
    </label>
  )
}

